import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import { ArrowRight, Stethoscope, Users, CheckCircle, MapPin, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useSiteSettings } from '../SiteSettingsContext';
const ReturningParticipantPage = () => {
  const { user, loginWithGoogle, loading: authLoading } = useAuth();
  const { logoUrl, navIconUrl, orgName } = useSiteSettings();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (user) fetchTrips();
  }, [user]);

  const fetchTrips = async () => {
    try {
      const res = await api.get('/trips?status=registration_open');
      setTrips(res.data);
    } catch {
      toast.error('Failed to load trips');
    }
  };

  const handleSubmitRequest = async () => {
    if (!selectedTrip) return;
    setSubmitting(true);
    try {
      await api.post('/trip-requests', { trip_id: selectedTrip.trip_id });
      setConfirmed(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            {logoUrl ? (
              <img src={logoUrl} alt={orgName} className="h-10 w-auto object-contain" />
            ) : (
              <>
                <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center">
                  <Stethoscope className="w-5 h-5 text-white" />
                </div>
                <span className="font-heading font-bold text-lg text-slate-800">{orgName || 'Guatemala Surgery'}</span>
              </>
            )}
          </Link>
          {user && (
            <span className="text-sm text-slate-500">Signed in as <strong>{user.name || user.email}</strong></span>
          )}
        </div>
      </nav>

      <div className="pt-28 pb-16 px-6 max-w-2xl mx-auto">

        {/* Step 1: Not signed in */}
        {!user && (
          <div className="text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Stethoscope className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-slate-800 mb-3">Returning Participant</h1>
            <p className="text-slate-600 mb-8 leading-relaxed">
              Sign in to request to join an active mission week. Your profile
              information will be pre-filled from your previous registration.
            </p>
            <Link to="/login?redirect=/returning">
              <Button size="lg" className="rounded-full bg-rojo hover:bg-rojo-dark px-10">
                Sign In to Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <p className="text-sm text-slate-400 mt-6">
              First time?{' '}
              <Link to="/signup" className="text-primary hover:underline">Sign up here</Link>
            </p>
          </div>
        )}

        {/* Step 2: Signed in — select trip */}
        {user && !confirmed && (
          <>
            <div className="mb-8">
              <h1 className="text-2xl font-heading font-bold text-slate-800 mb-2">
                Welcome back, {user.name?.split(' ')[0] || 'Volunteer'}!
              </h1>
              <p className="text-slate-600">Select the mission week you'd like to join.</p>
            </div>

            {trips.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <p className="text-slate-500">No active trips open for registration right now.</p>
                  <p className="text-slate-400 text-sm mt-2">Check back soon or contact us at guatemalasurgery@gmail.com</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {trips.map(trip => (
                  <button
                    key={trip.trip_id}
                    onClick={() => setSelectedTrip(trip)}
                    className={`w-full text-left rounded-xl border-2 p-5 transition-all ${
                      selectedTrip?.trip_id === trip.trip_id
                        ? 'border-primary bg-primary/5'
                        : 'border-slate-200 bg-white hover:border-primary/40'
                    }`}
                    data-testid={`trip-option-${trip.trip_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-slate-800 text-lg">{trip.name}</p>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />{trip.start_date} – {trip.end_date}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5" />{trip.location}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5" />{trip.participant_count || 0}/{trip.max_participants || 35} registered
                          </span>
                        </div>
                      </div>
                      {selectedTrip?.trip_id === trip.trip_id && (
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      )}
                    </div>
                  </button>
                ))}

                <Button
                  className="w-full rounded-full bg-rojo hover:bg-rojo-dark mt-4"
                  size="lg"
                  disabled={!selectedTrip || submitting}
                  onClick={handleSubmitRequest}
                  data-testid="submit-request-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                  {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmed} onOpenChange={() => {}}>
        <DialogContent className="max-w-md text-center" aria-describedby="confirm-desc">
          <DialogHeader>
            <DialogTitle className="text-center">Request Submitted!</DialogTitle>
          </DialogHeader>
          <div id="confirm-desc" className="py-4 space-y-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <p className="text-slate-700">
              Your request to join <strong>{selectedTrip?.name}</strong> has been submitted.
            </p>
            <p className="text-slate-500 text-sm">
              Our admin team will review your request and send a formal invitation.
              In the meantime, you can log in to review and update your profile.
            </p>
            <Button
              className="w-full rounded-full bg-rojo hover:bg-rojo-dark"
              onClick={() => navigate('/dashboard')}
              data-testid="go-to-dashboard-btn"
            >
              Go to My Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReturningParticipantPage;
